import React, { useEffect, useRef, useState } from 'react'
import { useStores } from '../../../stores/store'
import { CSVLink } from 'react-csv';
import { Table } from '../../common/table';
import { Pagination } from '../../common/pagination';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Popup from 'reactjs-popup';
import { useTranslation } from 'react-i18next';
import { FaEye } from 'react-icons/fa';
import Moment from 'moment';
import Loader from '../../common/loader';

const LS_KEY = 'admin_schools_pageSize';

function AdminSchoolsPage() {
  const { t } = useTranslation();
  let navigate = useNavigate();

  const { auth } = useStores();
  const searchKey = useRef<string>(localStorage.getItem('admin_schools_searchKey') || "");
  const searchRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [referralId, setReferralId] = useState(null);

  const [currentCountry, setCurrentCountry] = useState(null);
  const [countries, setCountries] = useState([]);
  const [isFilterError, setIsFilterError] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [isDataError, setIsDataError] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [schools, setSchools] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(LS_KEY);
    return saved ? +saved : 50;
  });
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    setReferralId(searchParams.get('referral_id'));

    setIsFilterLoading(true);
    setIsFilterError(false);

    auth.getCountries().then((res) => {
      setCountries(res.data);
      if (res.data.length > 0) {
        setCurrentCountry(res.data[0]);
      }
      setIsFilterLoading(false);
    }).catch((error) => {
      setIsFilterLoading(false);
      setIsFilterError(true);
    });
  }, [])

  useEffect(() => {
    getSchools();
  }, [currentCountry])

  const getSchools = (page = 1, size = pageSize) => {
    if (currentCountry == null)
      return;

    setIsDataLoading(true);
    setIsDataError(false);

    auth.getSchools(searchKey.current, currentCountry.id, page, size).then((res) => {
      setSchools(res.data.results || []);
      setTotalCount(res.data.count || 0);
      setCurrentPage(page);
      setIsDataLoading(false);
    }).catch((error) => {
      setIsDataLoading(false);
      setIsDataError(true);
    });
  }

  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    localStorage.setItem(LS_KEY, String(size));
    getSchools(1, size);
  };

  const columns = React.useMemo(
      () => [
      {
          Header: t('schools'),
          columns: [
            {
              Header: t('view'),
              accessor: "id",
              Cell: ({ cell }) => (
                <Link to={
                    `/admin/schools/${cell.row.values.id}`}>
                  <FaEye />
                </Link>
              )
            },
          {
              Header: t('name'),
              accessor: 'name',
          },
          {
            Header: t('mechanographicCode'),
            accessor: 'code',
          },
          {
            Header: t('users'),
            accessor: "users",
            Cell: props => {
              return props.value.length;
            }
          },
          {
            Header: t('devices'),
            accessor: "devices",
            Cell: props => {
              return props.value.length;
            }
          },
          {
            Header: "Newsletter?",
            accessor: "newsletter_consent",
            Cell: props => {
              return props.newsletter_consent ? t('yes') : t('no')
            }
          },
        ],
      },
      ],
      []
  );

  const selectedColumns = React.useMemo(
    () => [
      {
        Header: t('select'),
        accessor: "id",
        Cell: props => {
          return <button onClick={() => {setSelectedSchool(props.row.values.id); setShowConfirm(true);}} className='border-2 border-primary rounded-full p-2'>{t('select')}</button>
        }
      },
      {
        Header: t('schools'),
        columns: [
        {
            Header: t('name'),
            accessor: 'name',
        },
        {
          Header: t('mechanographicCode'),
          accessor: 'code',
        },
      ],
    },
    ], []
  );

  const search = event => {
    if (event.target.value && event.target.value.length < 4)
      return;

    searchKey.current = event.target.value;
    localStorage.setItem('admin_schools_searchKey', event.target.value);
    getSchools(1);
  }

  const save = () => {
    navigate(`/admin/schools/${selectedSchool}?referral_id=${referralId}`);
  }

  if (isFilterLoading)
    return <div className='grid h-screen place-items-center'><Loader /></div>

  if (isFilterError)
    return <div className='grid h-screen place-items-center'>{t('cannotLoadPage')}</div>

  const paginationControls = (
    <Pagination
      currentPage={currentPage}
      totalPages={totalPages}
      pageSize={pageSize}
      onPageChange={(page) => getSchools(page)}
      onPageSizeChange={handlePageSizeChange}
    />
  );

  return (
    <div className="flex flex-col gap pb-12">
      <Popup open={showConfirm}
          position="right center"
          closeOnDocumentClick
      >
          <div className="flex flex-col items-center gap-6 p-6">
              <span>{t('selectSchoolForDevice')}</span>
              <div className="flex flex-row items-center gap-6">
                  <button onClick={save} className="bg-primary text-white font-bold py-2 px-4 rounded-full">{t('yes')}</button>
                  <button onClick={(e) => { e.preventDefault(); setShowConfirm(false) }} className="border-2 border-primary text-primary font-bold py-2 px-4 rounded-full">{t('no')}</button>
              </div>
          </div>
      </Popup>

      <div className="flex flex-row justify-start">
        <span className="text-4xl">{t('schools')}</span>
      </div>
      <div className='flex flex-row mt-4 justify-between'>
        <div className='flex flex-row space-x-2'>
        <input onChange={search} ref={searchRef} defaultValue={searchKey.current} placeholder={t('searchMin4Chars')} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" />
        <CSVLink className="bg-primary hover:bg-blue-700 text-white font-bold p-4 rounded-full" data={schools} filename={`exportdata_${Moment().format("DD-MM-YYYY")}.csv`}>{t('export')}</CSVLink>
        </div>
      </div>

      {!isDataError && !isDataLoading && paginationControls}

      {isDataLoading && <div className='grid h-screen place-items-center'><Loader /></div> }

      {isDataError && <div className='grid h-screen place-items-center'>{t('cannotLoadPage')}</div> }

      {referralId == null && !isDataError && !isDataLoading && <Table columns={columns} data={schools} /> }

      {referralId != null && !isDataError && !isDataLoading && <Table columns={selectedColumns} data={schools} /> }

      {!isDataError && !isDataLoading && <div className="mt-4">{paginationControls}</div>}
    </div>
  )
}

export default AdminSchoolsPage
